<script setup lang="ts">
import usePoolStore from '~/store/pool'
import { UpdatePictureStatus } from '~/interfaces/picture'
import useUserStore from '~/store/user'
import PoolDoesNotHavePicture from '../Common/PoolDoesNotHavePicture.vue'
import IconRefresh from '../Icons/IconRefresh.vue'
import IconThumbUp from '../Icons/IconThumbUp.vue'
import IconThumbDown from '../Icons/IconThumbDown.vue'
import IconCamera from '../Icons/IconCamera.vue'
import IconEye from '../Icons/IconEye.vue'
import { getPhotoUrlBySize } from '#cmui/utils'
import { PhotoSizes } from '#cmui/types'

const { $throwError } = useNuxtApp()

const props = defineProps({
    isContainedInDrawer: {
        type: Boolean,
        default: true
    },
    isExploitation: {
        type: Boolean,
        required: true
    }
})

const emits = defineEmits([
    'pictureIndex',
    'showPicturePopUp',
    'sendPictures',
    'showUserNotConnectedModal'
])

const { locale } = useI18n()

const pictureRotation = ref([] as number[])
const pictureUpdateStatus = ref([] as number[])

const getPictures = computed(() => usePoolStore().poolPhotos || [])

const showStatus = (index: number) => {
    setTimeout(() => {
        pictureUpdateStatus.value[index] = UpdatePictureStatus.DEFAULT
    }, 2000)
}

const getRotationDegreeByClickNumber = (index: number) => {
    if (!pictureRotation.value[index]) {
        return 0
    }

    switch (pictureRotation.value[index]! % 4) {
        case 1:
            return 90
        case 2:
            return 180
        case 3:
            return 270
        default:
            return 0
    }
}

const onSendRotatedPicture = async (
    picture: any,
    blob: Blob,
    index: number
) => {
    try {
        const data: any = await usePoolStore().sendRotatedPicture(picture, blob)
        pictureUpdateStatus.value[index] =
            data?.response?.success === true
                ? UpdatePictureStatus.SUCCESS
                : UpdatePictureStatus.FAILURE
    } catch (e: any) {
        pictureUpdateStatus.value[index] = UpdatePictureStatus.FAILURE
        $throwError(
            e,
            `RequestFailed: sendRotatedPicture (${
                usePoolStore().poolId || 'unknown pool'
            })`
        )
    }
    showStatus(index)
}

const getMimeType = (pictureUrl: string): string => {
    const splitUrl = pictureUrl.split('.')

    return `image/${splitUrl[splitUrl.length - 1]}`
}

const rotatePictureAndConvertToBlob = (picture: any, index: number) => {
    if (!pictureRotation.value[index]) {
        return
    }

    pictureUpdateStatus.value[index] = UpdatePictureStatus.DOING
    const img = new Image()
    img.src = `/s3/charging_pool_photos/url/original/${picture.id}/${picture.url}`

    img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D

        if (pictureRotation.value[index]! % 2) {
            canvas.width = img.height
            canvas.height = img.width
        } else {
            canvas.width = img.width
            canvas.height = img.height
        }
        ctx.translate(canvas.width / 2, canvas.height / 2)
        ctx.rotate((getRotationDegreeByClickNumber(index) * Math.PI) / 180)
        ctx.drawImage(img, -img.width / 2, -img.height / 2)
        canvas.toBlob(
            (async (blob: Blob) => {
                await onSendRotatedPicture(picture, blob, index)
            }) as BlobCallback,
            getMimeType(img.src),
            1
        )
    }
}

const openPicture = (index: number) => {
    if (useUserStore().userId || props.isExploitation) {
        emits('sendPictures', usePoolStore().poolPhotos)
        emits('pictureIndex', index)
        emits('showPicturePopUp')
    } else if (index === 0) {
        emits('sendPictures', usePoolStore().poolPhotos?.[0])
        emits('pictureIndex', 0)
        emits('showPicturePopUp')
    } else {
        emits('showUserNotConnectedModal')
    }
}

const redirectOnAddPictures = () => {
    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            { action: 'openUrl', url: `wizard/${usePoolStore().poolId}#6` },
            document.referrer
        )
    } else {
        const newWnd = window.open(
            getChargemapUrl(`/wizard/${usePoolStore().poolId}#6`, locale.value),
            '_top'
        )

        if (newWnd) {
            newWnd.opener = null
        }
    }
}

const isPictureStatusDoing = (index: number): boolean =>
    pictureUpdateStatus.value[index] === UpdatePictureStatus.DOING

const isPictureStatusFailed = (index: number): boolean =>
    pictureUpdateStatus.value[index] === UpdatePictureStatus.FAILURE

const isPictureStatusSuccess = (index: number): boolean =>
    pictureUpdateStatus.value[index] === UpdatePictureStatus.SUCCESS

const isPictureStatusDefault = (index: number): boolean =>
    pictureUpdateStatus.value[index] === UpdatePictureStatus.DEFAULT ||
    !pictureUpdateStatus.value[index]

const rotatePicture = (picture: any, index: number) => {
    if (isPictureStatusDoing(index)) {
        return
    }

    if (pictureRotation.value[index]) {
        pictureRotation.value[index] += 1
    } else {
        pictureRotation.value[index] = 1
    }

    const photo = document.getElementById(picture.id)
    const rotateRegex = /(rotate-[\D]*[0-9]+[\D]*)$/
    let currentRotate = ''
    photo?.classList?.forEach((className: string) => {
        currentRotate = rotateRegex.test(className) ? className : ''
    })
    photo?.classList?.replace(
        currentRotate,
        `rotate-${getRotationDegreeByClickNumber(index)}`
    )
}

onMounted(() => {
    usePoolStore().fetchPoolPhotos()
})
</script>

<template>
    <div class="h-full">
        <Loader v-if="usePoolStore().communityPoolPhotosLoader" />
        <PoolDoesNotHavePicture
            v-else-if="!usePoolStore().poolPhotos.length"
            :is-contained-in-drawer
        />
        <div v-else class="text-sm p-5 flex flex-col">
            <div v-if="useUserStore().userId" class="mb-3.5 mx-auto w-fit">
                <ChargemapB2CButton
                    type="primary"
                    size="large"
                    @click="redirectOnAddPictures"
                >
                    <template #icon>
                        <IconCamera class="h-5" />
                    </template>
                    {{ $t('add_photos') }}
                </ChargemapB2CButton>
            </div>

            <div class="flex flex-wrap">
                <div
                    v-for="(photo, index) of getPictures"
                    :key="photo.id"
                    class="overflow-hidden rounded-lg h-full w-1/2 425:w-1/3 sm:w-1/4 p-1"
                    :class="{
                        'justify-start': !isContainedInDrawer
                    }"
                >
                    <div
                        class="relative left-0 h-full flex items-center justify-center cursor-pointer overflow-hidden rounded-lg"
                        @click="openPicture(index)"
                    >
                        <NuxtImg
                            :id="String(photo.id)"
                            width="160"
                            height="160"
                            class="rounded-lg transform transition-transform rotate-0 aspect-square object-cover"
                            loading="lazy"
                            :src="getPhotoUrlBySize(photo, PhotoSizes.SMALL)"
                            :class="{
                                'filter blur-sm bg-opacity-10 overflow-hidden':
                                    index !== 0 &&
                                    !useUserStore().userId &&
                                    !isExploitation
                            }"
                            :alt="usePoolStore().poolName"
                        />
                        <IconEye
                            v-if="
                                index !== 0 &&
                                !useUserStore().userId &&
                                !isExploitation
                            "
                            class="absolute text-white h-6"
                        />
                    </div>
                    <!--                    <div-->
                    <!--                        v-if="useUserStore().isAdmin"-->
                    <!--                        class="bg-white-s w-full h-[40px] rounded-lg flex justify-center items-center gap-9 mt-0.5 py-2 px-4"-->
                    <!--                    >-->
                    <!--                        <IconRefresh-->
                    <!--                            is-solid-->
                    <!--                            class="h-6 w-auto text-button-primary-branding-hover"-->
                    <!--                            :class="{-->
                    <!--                                'animate-spin cursor-default':-->
                    <!--                                    isPictureStatusDoing(index),-->
                    <!--                                'cursor-pointer': isPictureStatusDefault(index),-->
                    <!--                                hidden:-->
                    <!--                                    isPictureStatusFailed(index) ||-->
                    <!--                                    isPictureStatusSuccess(index)-->
                    <!--                            }"-->
                    <!--                            @click="rotatePicture(photo, index)"-->
                    <!--                        />-->
                    <!--                        <IconCheck-->
                    <!--                            class="h-3.5 w-auto cursor-pointer text-button-primary-branding-hover"-->
                    <!--                            is-solid-->
                    <!--                            :class="{-->
                    <!--                                hidden:-->
                    <!--                                    isPictureStatusFailed(index) ||-->
                    <!--                                    isPictureStatusSuccess(index) ||-->
                    <!--                                    isPictureStatusDoing(index)-->
                    <!--                            }"-->
                    <!--                            @click="rotatePictureAndConvertToBlob(photo, index)"-->
                    <!--                        />-->
                    <!--                        <IconThumbUp-->
                    <!--                            :class="{ hidden: !isPictureStatusSuccess(index) }"-->
                    <!--                            class="fill-current text-green-l-700 h-5 w-auto"-->
                    <!--                        />-->
                    <!--                        <IconThumbDown-->
                    <!--                            :class="{ hidden: !isPictureStatusFailed(index) }"-->
                    <!--                            class="fill-current text-red-tesla h-5 w-auto"-->
                    <!--                        />-->
                    <!--                    </div>-->
                </div>
            </div>
        </div>
    </div>
</template>
